import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { default as localeDeCH, default as localeFrCH } from '@angular/common/locales/de-CH';
import localeEnCH from '@angular/common/locales/en-CH';
import localeDeCHExtra from '@angular/common/locales/extra/de-CH';
import localeEnCHExtra from '@angular/common/locales/extra/en-CH';
import localeFrCHExtra from '@angular/common/locales/extra/fr-CH';
import localeItCHExtra from '@angular/common/locales/extra/it-CH';
import localeItCH from '@angular/common/locales/it-CH';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PreloadAllModules, provideRouter, Router, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { authenticationInterceptor } from '@core/interceptor/authentication.interceptor';
import { API_KEY, BACKEND_API } from '@core/tokens';
import { provideAuth } from '@features/auth/auth.provider';
import { provideTransloco } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { frCH } from 'date-fns/locale';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';

registerLocaleData(localeFrCH, 'fr', localeFrCHExtra);
registerLocaleData(localeDeCH, 'de', localeDeCHExtra);
registerLocaleData(localeEnCH, 'en', localeEnCHExtra);
registerLocaleData(localeItCH, 'it', localeItCHExtra);

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'dd.MM.yyyy',
  },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd MMMM yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'fr',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: frCH,
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    provideDateFnsAdapter(MY_DATE_FORMATS),
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'CHF',
    },
    {
      provide: BACKEND_API,
      useValue: environment.apiUrl,
    },
    {
      provide: API_KEY,
      useValue: environment.apiKey,
    },
    provideAuth(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    provideHttpClient(withInterceptors([authenticationInterceptor])), // create provider
    provideTransloco({
      config: {
        availableLangs: [
          { id: 'fr', label: 'Français' },
          { id: 'de', label: 'Deutsch' },
          { id: 'it', label: 'Italiano' },
          { id: 'en', label: 'English' },
        ],
        defaultLang: 'fr',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        flatten: {
          aot: !isDevMode(),
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideAnimationsAsync(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:10000',
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
